/* eslint-disable react-refresh/only-export-components */


import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/productionLogger';

interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  position: string;
  avatar_url?: string | null;
  organization_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, company: string, position: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
  isAuthenticated: boolean;
  refreshSession: () => Promise<void>;
  isRecoveryMode: boolean;
  clearRecoveryMode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  
  // ✅ Verrou pour éviter les validations simultanées (race condition)
  const validatingRef = React.useRef(false);

  const fetchProfile = useCallback(async (userId: string, retries = 3): Promise<Profile | null> => {
    try {
      logger.debug('Fetching profile for user', { userId, attempt: 4 - retries });
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Profil n'existe pas encore
          if (retries > 0) {
            logger.debug('Profile not found, retrying in 1 second');
            await new Promise(resolve => setTimeout(resolve, 1000));
            return fetchProfile(userId, retries - 1);
          } else {
            logger.warn('Profile not found after retries');
            return null;
          }
        }
        logger.error('Error fetching profile', error);
        throw error;
      }

      const profile = data as Profile;
      logger.debug('Profile fetched successfully', { profileId: profile.id });
      setProfile(profile);
      return profile;
    } catch (_error) {
      logger.error('Unexpected error fetching profile', _error as Error);
      if (retries > 0) {
        logger.debug('Retrying profile fetch', { retriesLeft: retries });
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchProfile(userId, retries - 1);
      }
      throw _error;
    }
  }, []);

  /**
   * ✅ Validation serveur de la session (anti ghost session)
   * Vérifie que la session client est toujours valide côté serveur via une requête RLS
   */
  const validateServerSession = useCallback(async (userId: string) => {
    // Verrou pour éviter les validations simultanées
    if (validatingRef.current) {
      logger.debug('Server session validation already in progress, skipping');
      return;
    }
    
    validatingRef.current = true;
    
    try {
      logger.debug('Validating server session', { userId });
      
      // Requête canary minimale pour tester RLS
      const { error } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();

      if (error) {
        const code = (error as any)?.code;
        const msg = ((error as any)?.message ?? '').toLowerCase();

        // Détecter les erreurs d'authentification (RLS, JWT, etc.)
        const looksLikeAuthIssue =
          code === 'PGRST301' ||
          msg.includes('rls') ||
          msg.includes('jwt') ||
          msg.includes('not authorized');

        if (looksLikeAuthIssue) {
          logger.warn('Server session invalid (ghost session detected). Signing out.', { code, msg });
          await supabase.auth.signOut();
        } else {
          logger.debug('Server session validation returned non-auth error', { code, msg });
        }
      } else {
        logger.debug('Server session validated successfully');
      }
    } catch (e) {
      // Erreur réseau ≠ session invalide : on ne déconnecte pas
      logger.debug('Server session validation failed (network?)', { error: e });
    } finally {
      validatingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Vérifier si l'URL contient des paramètres de recovery au chargement
    const urlParams = new URLSearchParams(window.location.search);
    const isRecovery = urlParams.get('type') === 'recovery';
    if (isRecovery) {
      logger.debug('Recovery mode detected from URL');
      setIsRecoveryMode(true);
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('Auth state change', { event, userId: session?.user?.id });
        
        // Détecter le mode recovery
        if (event === 'PASSWORD_RECOVERY') {
          logger.debug('Password recovery mode activated');
          setIsRecoveryMode(true);
        }
        
        // Gérer les erreurs de token
        if (event === 'TOKEN_REFRESHED') {
          logger.debug('Token refreshed successfully');
        } else if (event === 'SIGNED_OUT') {
          logger.debug('User signed out');
          setProfile(null);
          setIsRecoveryMode(false);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        

        
        if (session?.user) {
          fetchProfile(session.user.id).catch(error => {
            logger.error('Failed to fetch profile on auth change', error as Error);
          });
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id).catch(error => {
          logger.error('Failed to fetch profile on initial load', error as Error);
        });
      }
      
      setLoading(false);
    });

    // ✅ Event-driven ghost session detection
    // Valider la session serveur quand l'utilisateur revient sur l'onglet
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!user?.id) return;
      
      logger.debug('Tab visible/focus → validating server session');
      await validateServerSession(user.id);
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      subscription.unsubscribe();
    };
  }, [fetchProfile, validateServerSession, user?.id]);

  const signUp = useCallback(async (email: string, password: string, firstName: string, lastName: string, company: string, position: string) => {
    // Rediriger vers la page de login avec un paramètre de confirmation
    const redirectUrl = `${window.location.origin}/auth?confirmed=true`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
          company: company,
          position: position
        }
      }
    });
    
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    try {
      logger.info('User signing out');
      await supabase.auth.signOut();
      setIsRecoveryMode(false);
    } catch (_error) {
      logger.error('Error during sign out', _error as Error);
      throw _error;
    }
  }, []);

  const clearRecoveryMode = useCallback(() => {
    logger.debug('Clearing recovery mode');
    setIsRecoveryMode(false);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error('Error refreshing session', error);
        throw error;
      }

      if (session) {
        setSession(session);
        setUser(session.user);
      }
    } catch (_error) {
      logger.error('Error refreshing session', _error as Error);
      throw _error;
    }
  }, []);



  const resetPassword = useCallback(async (email: string) => {
    // Construire l'URL de redirection complète
    const baseUrl = window.location.origin;
    const redirectUrl = `${baseUrl}/auth/reset-password`;
    
    // Log pour déboguer l'URL envoyée
    
    
    logger.debug('Sending password reset email', { 
      email, 
      redirectUrl,
      encodedUrl: encodeURIComponent(redirectUrl)
    });
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    
    if (error) {
      logger.error('Error sending password reset email', error);
    } else {
      logger.info('Password reset email sent successfully', { email });
    }
    
    return { error };
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      try {
        await fetchProfile(user.id);
      } catch (_error) {
        logger.error('Failed to refresh profile', _error as Error);
      }
    }
  }, [user, fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('No user logged in') };

    // Utiliser une requête typée manuellement pour contourner le problème de typage
    const { error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      } as Record<string, any>)
      .eq('id', user.id);

    if (!error) {
      await refreshProfile();
    }

    return { error };
  }, [user, refreshProfile]);

  // Valeurs memoizées pour éviter les re-renders inutiles
  const contextValue = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user,
    refreshSession,
    isRecoveryMode,
    clearRecoveryMode
  }), [user, session, profile, loading, signUp, signIn, signOut, resetPassword, updateProfile, refreshProfile, refreshSession, isRecoveryMode, clearRecoveryMode]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
